import { Bouton } from '../ui/composants/bouton'
import { useRef, useState } from 'react'
import Decimal from 'decimal.js'
import { annulerFacture, facturerAcompte, facturerDevis, marquerPaiement, reglerFacture } from '../db/sync'
import type { FactureDevis, Reglement } from '../db/modele'
import { acompteDepuisMontant } from '../lib/acomptes'
import { acomptesEnVigueur, acomptesFacturesPercent, acomptesImpayes, donneesPdfFacture, factureEnVigueur, lignesAcompte, totauxDevis } from '../lib/devis'
import { lireEntreprise } from '../lib/entreprise'
import { xmlCii } from '../lib/facturx/cii'
import { factureDeDevis } from '../lib/facturx/devis'
import { nouvelId } from '../lib/id'
import { useImpayes } from '../ui/impayes'
import { eurosVersCentimes, formatEuro, lireNombre, totalDocument } from '../lib/money'
import { versCalcul } from '../lib/pdf/devis-pdf'
import { genererFactureDevis } from '../lib/pdf/generer'
import { getBase, type ClientLocal, type DevisLocal } from '../outbox/base'
import { ErreurFinalisation } from '../outbox/synchro'
import { ApercuPdf } from '../ui/apercu-pdf'
import { Paiement } from '../ui/paiement'
import { ErreurFabrication, Transmission, useTransmissions } from '../ui/transmission'
import { CHAMP } from '../ui/synchro'

const MESSAGES: Record<string, string> = {
  hors_ligne: 'Le numéro de facture est attribué par le serveur : il faut du réseau.',
  non_connecte: 'Connectez-vous (menu Compte) : le numéro de facture est attribué par le serveur.',
  abonnement_requis: 'Abonnement de votre entreprise expiré : cette action est indisponible.',
  devis_non_signe: 'Seul un devis signé peut être facturé.',
  devis_en_situations: 'Ce devis est facturé en situations de travaux : pas de facture directe.',
  deja_facture: 'Ce devis a déjà une facture en vigueur.',
  devis_vide: 'Ce devis n’a aucune ligne.',
  avenant_en_situations: 'Les situations de travaux du marché ont commencé : cet avenant en fait partie et se facture avec elles.',
  acompte_excessif: 'Le total des acomptes dépasserait 100 % du devis.',
  acompte_deduit: 'Cet acompte est déduit de la facture de solde : annulez d’abord la facture de solde.',
  requete_invalide: 'Pourcentage ou date invalide.',
  acompte_impaye: 'Un acompte n’est pas encore marqué payé : la facture de solde le déduit comme déjà versé. Marquez-le payé ou annulez-le.',
  date_invalide: 'Date de paiement invalide : ni dans le futur, ni avant la facture.',
  facture_annulee: 'Cette facture est annulée par un avoir.',
}

const dateFr = (iso: string) => new Date(iso).toLocaleDateString('fr-FR')

/**
 * Facture directe d'un devis signé (hors situations) : émission, Factur-X, avoir total. Un devis est facturé
 * en une fois ou en situations de travaux ; le devis lui-même n'est jamais modifié.
 */
export function FacturationDevis(props: { devis: DevisLocal; client: ClientLocal | null; situations: number; franchise: boolean }) {
  const { devis: d } = props
  const base = getBase()
  const [enCours, setEnCours] = useState(false)
  const [erreur, setErreur] = useState<string | null>(null)
  const [avis, setAvis] = useState<string | null>(null)
  const [fichier, setFichier] = useState<{ pdf: Blob; libelle: string } | null>(null)
  // Une clé par demande : si la réponse se perd, relancer ne crée pas une seconde facture
  const tousImpayes = useImpayes()
  const dernierReglement = useRef<Reglement | null>(null)
  const demande = useRef(nouvelId())
  const demandeAcompte = useRef(nouvelId())
  const [pourcentage, setPourcentage] = useState('30')
  const [mode, setMode] = useState<'pourcentage' | 'montant'>('pourcentage')
  const [montant, setMontant] = useState('')

  const connues = useTransmissions()
  const factures = d.factures ?? []
  const enVigueur = factureEnVigueur(d)
  const facturable = d.statut === 'signe' && !enVigueur && props.situations === 0
  const dejaAcompte = acomptesFacturesPercent(d)
  const acomptes = acomptesEnVigueur(d)
  const impayes = acomptesImpayes(d)
  // Montant annoncé (le serveur fait foi) : acompte demandé, et solde = devis moins acomptes en vigueur
  // Acompte en montant TTC : on en tire le pourcentage le plus proche (le serveur n'en connaît pas d'autre)
  const centsMontant = mode === 'montant' ? eurosVersCentimes(montant) : null
  const enMontant = centsMontant ? acompteDepuisMontant(d.lignes, centsMontant, new Decimal(100).minus(dejaAcompte)) : null
  const pct = mode === 'montant' ? (enMontant?.pourcentage ?? null) : lireNombre(pourcentage)
  const pctValide = pct !== null && pct.greaterThan(0) && pct.plus(dejaAcompte).lessThanOrEqualTo(100) && pct.decimalPlaces() <= 2
  const apercuAcompte = pct && pctValide ? totalDocument(lignesAcompte(d.lignes, pct, d.numero).map(versCalcul)) : null
  const ttcAcomptes = acomptes.reduce((s, a) => s.plus(totalDocument((a.lignes ?? []).map((l) => versCalcul(props.franchise ? { ...l, tvaPercent: 0 } : l))).ttc), new Decimal(0))
  if (d.statut !== 'signe' || (!factures.length && !facturable)) return null

  const echec = (e: unknown) => setErreur(MESSAGES[e instanceof ErreurFinalisation ? e.code : 'reseau'] ?? 'L’opération a échoué. Réessayez avec du réseau.')

  async function facturer() {
    if (!confirm(`Émettre la facture de ce devis ? Le montant est celui du devis, le numéro est définitif et la facture ne pourra plus être modifiée (seulement annulée par un avoir).`)) return
    setEnCours(true)
    setErreur(null)
    try {
      await facturerDevis(d.id, demande.current)
      demande.current = nouvelId()
    } catch (e) {
      echec(e)
    } finally {
      setEnCours(false)
    }
  }

  async function acompte() {
    if (!pct || !pctValide) return
    if (!confirm(`Émettre une facture d’acompte de ${String(pct).replace('.', ',')} % (${formatEuro(apercuAcompte!.ttc)} TTC) ? Le numéro est définitif : elle ne pourra plus être modifiée, seulement annulée par un avoir.`)) return
    setEnCours(true)
    setErreur(null)
    try {
      await facturerAcompte(d.id, demandeAcompte.current, pct.toNumber())
      demandeAcompte.current = nouvelId()
    } catch (e) {
      echec(e)
    } finally {
      setEnCours(false)
    }
  }

  async function payer(f: FactureDevis, payeLe: string | null) {
    setEnCours(true)
    setErreur(null)
    try {
      await marquerPaiement(f.id, payeLe)
    } catch (e) {
      echec(e)
    } finally {
      setEnCours(false)
    }
  }

  // Un même règlement renvoyé après une réponse perdue garde son identifiant : le serveur ne le note qu'une fois
  function reglement(date: string, montantCents: number): Reglement {
    const d = dernierReglement.current
    return d && d.date === date && d.montantCents === montantCents ? d : (dernierReglement.current = { id: nouvelId(), date, montantCents })
  }

  async function regler(f: FactureDevis, action: { ajout: Reglement } | { retraitId: string }) {
    setEnCours(true)
    setErreur(null)
    try {
      await reglerFacture(f.id, action)
      dernierReglement.current = null
    } catch (e) {
      echec(e)
    } finally {
      setEnCours(false)
    }
  }

  async function annuler(f: FactureDevis) {
    if (!confirm(`Émettre un avoir qui annule la facture ${f.numero} ? C’est définitif : vous pourrez ensuite refacturer le devis.`)) return
    setEnCours(true)
    setErreur(null)
    try {
      await annulerFacture(f.id)
    } catch (e) {
      echec(e)
    } finally {
      setEnCours(false)
    }
  }

  /** Factur-X d'une facture (ou de son avoir), fabriqué sur l'appareil ; lève `ErreurFabrication` si les données manquent. */
  async function fabriquer(f: FactureDevis, avoir?: FactureDevis['avoir']): Promise<{ pdf: Blob; libelle: string; avis?: string }> {
    const entreprise = await lireEntreprise(base)
    if (!entreprise) throw new ErreurFabrication('Renseignez d’abord les réglages de l’entreprise (menu Réglages) : la facture électronique en a besoin.')
    if (!props.client) throw new ErreurFabrication('Le client de ce devis est introuvable.')
    const resultat = factureDeDevis(d, f, props.client, entreprise, avoir)
    if (!resultat.ok) throw new ErreurFabrication(`Facture impossible, il manque : ${resultat.manques.join(' ; ')}.`)
    let avis: string | undefined
    if (resultat.ecartTtcCents !== 0) {
      const n = Math.abs(resultat.ecartTtcCents)
      avis = `La facture électronique calcule la TVA par taux, comme la norme l’exige : elle diffère de ${n} centime${n > 1 ? 's' : ''} du total TTC affiché à l’écran.`
    }
    const donnees = donneesPdfFacture(d, f, props.client, entreprise, avoir, entreprise.franchiseTva)
    return { pdf: await genererFactureDevis(donnees, xmlCii(resultat.facture), entreprise.nom), libelle: `${avoir ? 'Avoir' : 'Facture'} ${donnees.numero}`, avis }
  }

  async function voir(f: FactureDevis, avoir?: FactureDevis['avoir']) {
    setErreur(null)
    setAvis(null)
    try {
      const r = await fabriquer(f, avoir)
      if (r.avis) setAvis(r.avis)
      setFichier({ pdf: r.pdf, libelle: r.libelle })
    } catch (e) {
      setErreur(e instanceof ErreurFabrication ? e.message : 'La facture électronique n’a pas pu être générée. Réessayez ; rien n’est modifié.')
    }
  }

  /** Fabrique le Factur-X à transmettre ; toute panne autre qu'un manque de données devient un message lisible. */
  const produire = (f: FactureDevis, avoir?: FactureDevis['avoir']) => async () => {
    try {
      return (await fabriquer(f, avoir)).pdf
    } catch (e) {
      throw e instanceof ErreurFabrication ? e : new ErreurFabrication('La facture électronique n’a pas pu être générée. Réessayez ; rien n’est modifié.')
    }
  }

  return (
    <section className="flex flex-col gap-3 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm" aria-label="Facturation du devis">
      <h3 className="font-bold">Facturation</h3>
      {facturable && (
        <>
          <p className="text-sm text-slate-700">
            Facturez ce devis en une fois, au montant du devis. Pour un chantier en plusieurs étapes, utilisez plutôt les situations de travaux.
          </p>
          {impayes.length > 0 && (
            <p className="rounded-2xl border-2 border-nuit bg-slate-100 p-3 text-sm" role="status">
              Avant la facture de solde, marquez payé{impayes.length > 1 ? 's' : ''} {impayes.length > 1 ? 'les acomptes' : 'l’acompte'} {impayes.map((a) => a.numero).join(', ')} (ou annulez-{impayes.length > 1 ? 'les' : 'le'}) : {impayes.length > 1 ? 'ils sont déduits' : 'il est déduit'} comme déjà versé{impayes.length > 1 ? 's' : ''}.
            </p>
          )}
          <Bouton className="flex-1" onClick={facturer} disabled={enCours || impayes.length > 0}>
            {enCours ? 'Émission…' : acomptes.length ? `Facturer le solde : ${formatEuro(totauxDevis(d).ttc.minus(ttcAcomptes))} TTC (réseau)` : 'Facturer ce devis (réseau)'}
          </Bouton>
          {dejaAcompte < 100 && (
            <div className="flex flex-col gap-2 rounded-2xl bg-slate-100 p-3">
              <div role="radiogroup" aria-label="Façon de fixer l’acompte" className="grid grid-cols-2 gap-1">
                {(['pourcentage', 'montant'] as const).map((m) => (
                  <button key={m} type="button" role="radio" aria-checked={mode === m} onClick={() => setMode(m)} className={`min-h-12 rounded-xl border-2 px-2 text-sm font-semibold ${mode === m ? 'border-nuit bg-nuit text-white' : 'border-slate-300 bg-white'}`}>
                    {m === 'pourcentage' ? 'En pourcentage' : 'En montant TTC'}
                  </button>
                ))}
              </div>
              {mode === 'pourcentage' ? (
                <label className="flex flex-col gap-1 text-sm">
                  <span className="text-slate-700">Acompte (% du devis{dejaAcompte > 0 ? `, ${String(dejaAcompte).replace('.', ',')} % déjà facturés` : ''})</span>
                  <input className={`${CHAMP} text-right ${pctValide ? '' : 'border-red-700'}`} inputMode="decimal" aria-label="Pourcentage d’acompte" value={pourcentage} aria-invalid={pctValide ? undefined : true} onChange={(e) => setPourcentage(e.target.value)} />
                </label>
              ) : (
                <label className="flex flex-col gap-1 text-sm">
                  <span className="text-slate-700">Acompte souhaité (€ TTC{dejaAcompte > 0 ? `, ${String(dejaAcompte).replace('.', ',')} % déjà facturés` : ''})</span>
                  <input className={`${CHAMP} text-right ${pctValide || montant.trim() === '' ? '' : 'border-red-700'}`} inputMode="decimal" aria-label="Montant TTC de l’acompte" value={montant} aria-invalid={pctValide || montant.trim() === '' ? undefined : true} onChange={(e) => setMontant(e.target.value)} />
                </label>
              )}
              <p className="text-sm" aria-live="polite">
                {apercuAcompte && pct ? (
                  <>
                    Acompte{mode === 'montant' && <> de {String(pct).replace('.', ',')} % du devis</>} : <strong>{formatEuro(apercuAcompte.ht)} HT</strong>, {formatEuro(apercuAcompte.ttc)} TTC.
                    {mode === 'montant' && enMontant && enMontant.ecartCents !== 0 && <> Écart avec votre montant : {enMontant.ecartCents > 0 ? '+' : '−'}{formatEuro(Math.abs(enMontant.ecartCents) / 100)} (le pourcentage a deux décimales).</>}
                  </>
                ) : mode === 'montant' ? (
                  montant.trim() === '' ? 'Saisissez le montant TTC à facturer en acompte.' : 'Montant illisible, supérieur au devis ou à ce qui reste à facturer.'
                ) : (
                  `Entre 0 et ${String(new Decimal(100).minus(dejaAcompte)).replace('.', ',')} %, deux décimales au plus.`
                )}
              </p>
              <Bouton variante="secondaire" onClick={acompte} disabled={enCours || !pctValide}>
                Facturer l’acompte (réseau)
              </Bouton>
            </div>
          )}
        </>
      )}
      {factures.length > 0 && (
        <ul className="flex flex-col gap-3">
          {factures.map((f) => (
            <li key={f.id} className="flex flex-col gap-2 rounded-2xl bg-slate-100 p-3 text-sm">
              <span className="font-semibold">
                {f.acomptePercent !== undefined ? `Facture d’acompte ${f.numero} (${String(f.acomptePercent).replace('.', ',')} %)` : `Facture ${f.numero}`} · {dateFr(f.emisLe)}
              </span>
              {f.avoir && (
                <span className="text-slate-700">
                  Annulée par l’avoir {f.avoir.numero} · {dateFr(f.avoir.emisLe)}
                </span>
              )}
              {!f.avoir && (
                <Paiement libelle={`la facture ${f.numero}`} emisLe={f.emisLe}
                  payeLe={f.payeLe}
                  occupe={enCours}
                  marquer={(date) => payer(f, date)}
                  retirer={() => payer(f, null)}
                  reglements={f.reglements}
                  reste={tousImpayes?.find((i) => i.id === f.id)?.montant.toNumber()}
                  regler={(date, montantCents) => regler(f, { ajout: reglement(date, montantCents) })}
                  retirerReglement={(id) => regler(f, { retraitId: id })}
                />
              )}
              <div className="flex flex-wrap gap-2">
                <Bouton variante="secondaire" onClick={() => voir(f)}>
                  Facture électronique
                </Bouton>
                {f.avoir && (
                  <Bouton variante="secondaire" onClick={() => voir(f, f.avoir)}>
                    Avoir
                  </Bouton>
                )}
              </div>
              <Transmission documentId={f.id} libelle={`la facture ${f.numero}`} connues={connues} produire={produire(f)} />
              {f.avoir && <Transmission documentId={f.avoir.id} libelle={`l’avoir ${f.avoir.numero}`} connues={connues} produire={produire(f, f.avoir)} />}
              {!f.avoir && (f.id === enVigueur?.id || (f.acomptePercent !== undefined && !enVigueur)) && (
                <Bouton variante="lienDanger" className="self-start" onClick={() => annuler(f)} disabled={enCours}>
                  Corriger : émettre un avoir qui annule cette facture
                </Bouton>
              )}
            </li>
          ))}
        </ul>
      )}
      {erreur && (
        <p className="rounded border-2 border-red-700 bg-red-50 p-3 text-sm text-red-900" role="alert">
          {erreur}
        </p>
      )}
      {avis && (
        <p className="rounded-2xl border-2 border-nuit bg-slate-100 p-3 text-sm" role="status">
          {avis}
        </p>
      )}
      {fichier && <ApercuPdf pdf={fichier.pdf} titre="Aperçu de la facture" nom={`${fichier.libelle}.pdf`} sujet={fichier.libelle} destinataire={props.client?.email} fermer={() => setFichier(null)} />}
    </section>
  )
}
